<script setup lang="ts">
  import { ref, computed, onMounted, type PropType } from 'vue'
  import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import type { CustomerQuestionnaire } from '@/types/generated'

  const props = defineProps({
    questionnaires: {
      type: Array as PropType<CustomerQuestionnaire[]>,
      default: () => []
    },
    selectedQuestionnaireIds: {
      type: Array,
      default: () => []
    }
  })

  const emit = defineEmits(['update:selectedQuestionnaires'])

  const searchQuery = ref('')
  const selectedQuestionnaireIds = computed(() => new Set(props.selectedQuestionnaireIds))

  const filteredQuestionnaires = computed<CustomerQuestionnaire[]>(() => {
    return props.questionnaires.filter((doc: any) =>
      doc.title.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  })

  const selectedCount = computed(() => selectedQuestionnaireIds.value.size)

  const isAllSelected = computed(() => {
    return selectedCount.value === props.questionnaires.length && props.questionnaires.length > 0
  })

  const toggleAllQuestionnaires = () => {
    const newSelectedIds = isAllSelected.value ? [] : props.questionnaires.map((doc: any) => doc.id)
    emit('update:selectedQuestionnaires', newSelectedIds)
  }

  const toggleQuestionnaire = (docId: any) => {
    const newSelectedIds = new Set(selectedQuestionnaireIds.value)
    if (newSelectedIds.has(docId)) {
      newSelectedIds.delete(docId)
    } else {
      newSelectedIds.add(docId)
    }
    emit('update:selectedQuestionnaires', Array.from(newSelectedIds))
  }
</script>

<template>
  <div class="mt-6 border border-gray-300 bg-white shadow sm:rounded-lg">
    <div class="p-4">
      <h3 class="mb-2 text-lg font-semibold text-gray-900">Fragebogen</h3>
      <div class="relative mb-2">
        <InputField
          v-model="searchQuery"
          name="questionnaire-search"
          placeholder="Fragebogen suchen"
        >
          <template #append>
            <MagnifyingGlassIcon
              class="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400"
            />
          </template>
        </InputField>
      </div>
      <div class="flex items-center justify-between">
        <label class="flex items-center">
          <input
            type="checkbox"
            :checked="isAllSelected"
            @change="toggleAllQuestionnaires"
            class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <span class="ml-2 text-sm font-medium text-gray-400">Alle auswählen</span>
        </label>
        <span class="text-sm text-gray-500">{{ selectedCount }} ausgewählt</span>
      </div>
      <div class="border-t border-gray-200"></div>
      <div class="max-h-60 overflow-y-auto">
        <template
          v-for="(doc, index) in filteredQuestionnaires"
          :key="doc.uuid"
        >
          <div class="">
            <label class="flex items-center">
              <input
                type="checkbox"
                :checked="selectedQuestionnaireIds.has(doc.uuid)"
                @change="toggleQuestionnaire(doc.uuid)"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span class="ml-2 text-sm font-medium text-gray-700">{{ doc.title }}</span>
            </label>
          </div>
          <div
            v-if="index < filteredQuestionnaires.length - 1"
            class="border-t border-gray-200"
          ></div>
        </template>
      </div>
    </div>
  </div>
</template>
